import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
    keyarData: []
}

// Same shape/logic as keyarData.js's helpers, but this slice only ever
// holds WebSocket-sourced data — nothing here is written by REST, so a
// full-replace REST dispatch elsewhere can never clobber it.
function getOrCreateEntry(state, patientID, defaults = {}) {
    let entry = state.keyarData.find((p) => p.patientID === patientID);
    if (!entry) {
        entry = {
            patientName: "",
            keyarData: [],
            patientID,
            session_time: null,
            place_id: null,
            hospitalName: "",
            hospitalLogo: null,
            session_uuid: null,
            lastFrameAt: null,
            ...defaults,
        };
        state.keyarData.push(entry);
    }
    return entry;
}

// latest_data only ever gets added to, so without a cap it grows for the
// whole session and every consumer re-sorts/stringifies all of it on every
// frame. Older history lives in IndexedDB (chartLayout2), not here.
// latest_data holds up to LIVE_RETENTION_SEC of points (~7k keys per param, x
// 4 params x every patient). As a plain object, Immer would shallow-copy and
// deep-freeze it on every update — measured at ~1s of CPU per second of data
// with 12 patients and a full window. An instance of a non-plain class is not
// "draftable", so Immer stores it by reference and the reducer appends in
// place; the param entry's lastPointAt changes on every merge so the param,
// its array and the patient entry still get new references for subscribers.
function LiveSeries() {}

const LIVE_RETENTION_SEC = 1800;
// Pruning walks every key of latest_data (through an Immer draft, which is
// slow), so do it once per this many seconds of incoming data per
// patient+param rather than on every sample — at ~4 pts/s that is roughly one
// pass per minute instead of one per frame. latest_data can therefore
// overshoot the retention window by up to this much.
const LIVE_PRUNE_EVERY_SEC = 60;
const lastPruneAt = new Map();

const toSeconds = (ts) => (String(ts).length === 13 ? Number(ts) / 1000 : Number(ts));

function pruneLatestData(latest, pruneKey, incoming) {
    let newest = 0;
    for (const k of Object.keys(incoming || {})) {
        const t = toSeconds(k);
        if (t > newest) newest = t;
    }
    if (!newest) return;
    const mark = lastPruneAt.get(pruneKey);
    if (mark !== undefined && newest >= mark && newest - mark < LIVE_PRUNE_EVERY_SEC) return;
    lastPruneAt.set(pruneKey, newest);

    const cutoff = newest - LIVE_RETENTION_SEC;
    Object.keys(latest).forEach((k) => {
        if (toSeconds(k) < cutoff) delete latest[k];
    });
}

function mergeParamData(entry, { param_type, data_points, meta_data, serial_number }) {
    let paramEntry = entry.keyarData.find((p) => p.param_type === param_type);
    if (!paramEntry) {
        // source: "ws" lets consumers tell WS-only data from REST-sourced
        // data (which still goes through the legacy replay path).
        paramEntry = { param_type, latest_data: new LiveSeries(), meta_data: {}, serial_number: null, source: "ws", lastPointAt: 0 };
        entry.keyarData.push(paramEntry);
    }
    Object.assign(paramEntry.latest_data, data_points || {});
    paramEntry.lastPointAt = Date.now();
    // Newest point's key, so readers needing "the latest value" don't have to
    // scan thousands of keys every tick.
    let newestK = paramEntry.newestKey;
    let newestT = newestK === undefined ? -Infinity : toSeconds(newestK);
    for (const k of Object.keys(data_points || {})) {
        const t = toSeconds(k);
        if (t > newestT) {
            newestT = t;
            newestK = k;
        }
    }
    if (newestK !== undefined) paramEntry.newestKey = newestK;
    pruneLatestData(paramEntry.latest_data, `${entry.patientID}|${param_type}`, data_points);
    if (meta_data) Object.assign(paramEntry.meta_data, meta_data);
    if (serial_number) paramEntry.serial_number = serial_number;
}

function mergeSample(state, payload) {
    const { patient_id, session_uuid, param_type, data_points, meta_data, serial_number, place_id } = payload;

    const timestamps = Object.keys(data_points).map(Number);
    const earliestTimestamp = timestamps.length > 0 ? Math.min(...timestamps) : null;
    const now = Date.now();

    const isUnassigned = patient_id === 0;
    const lookupKey = isUnassigned ? session_uuid : patient_id;

    let entry = null;
    let wasRelinked = false;
    if (!isUnassigned && session_uuid) {
        const relinkedEntry = state.keyarData.find(
            (p) => p.session_uuid === session_uuid && p.patientID !== patient_id
        );
        if (relinkedEntry) {
            relinkedEntry.patientID = patient_id;
            entry = relinkedEntry;
            wasRelinked = true;
        }
    }

    if (!entry) {
        entry = getOrCreateEntry(state, lookupKey, { session_time: earliestTimestamp, session_uuid });
    }

    // A different session_uuid than what's already stored for this
    // same patientID (and this isn't the unassigned→assigned relink
    // just above, which intentionally preserves data) means a new
    // monitoring session started for them — e.g. an earlier test
    // ended and a new one began, same patient. Without this, the
    // previous session's accumulated per-param data would keep
    // merging into the new one forever, since mergeParamData only
    // ever adds points, never clears them.
    if (
        !wasRelinked &&
        !isUnassigned &&
        entry.session_uuid &&
        session_uuid &&
        entry.session_uuid !== session_uuid
    ) {
        entry.keyarData = [];
        entry.session_time = earliestTimestamp;
    }

    if (session_uuid) entry.session_uuid = session_uuid;
    if (place_id) entry.place_id = place_id;
    entry.lastFrameAt = now;

    mergeParamData(entry, { param_type, data_points, meta_data, serial_number });
}

const liveKeyarDataSlice = createSlice({
    name: 'liveKeyarData',
    initialState,
    reducers: {
        // Moved here unchanged from keyarData.js, including the
        // session_uuid keying/re-keying for emergency/unassigned
        // monitoring — same tested behavior, just writing to its own slice.
        mergeLiveKeyarSample: (state, action) => {
            mergeSample(state, action.payload);
        },
        // Same as above for a whole batch of samples in one dispatch — the
        // socket handler coalesces frames so N patients x 4 channels at ~1 Hz
        // is a handful of store updates per second, not dozens.
        mergeLiveKeyarSamples: (state, action) => {
            action.payload.forEach((sample) => mergeSample(state, sample));
        },
        // Removes any entry with no frame in the last thresholdMs — used by
        // the periodic staleness sweep so a card disappears even when no
        // new frame ever arrives to trigger a re-render otherwise.
        pruneStaleLiveSessions: (state, action) => {
            const { thresholdMs, now = Date.now() } = action.payload;
            state.keyarData = state.keyarData.filter(
                (p) => p.lastFrameAt !== null && now - p.lastFrameAt <= thresholdMs
            );
        },
    }
})

export const { mergeLiveKeyarSample, mergeLiveKeyarSamples, pruneStaleLiveSessions } = liveKeyarDataSlice.actions;
export default liveKeyarDataSlice.reducer;

// Memoized selector: Union of REST-sourced patients (state.keyarData) plus
// WS-sourced patients (state.liveKeyarData), never mutating either source.
// A patientID present in both (edge case) gets REST's profile fields with
// WS's params merged on top, since WS is fresher.
//
// ⚠️ PERFORMANCE FIX: Using createSelector prevents re-renders when the
// actual merged data hasn't changed, even though Redux state object
// references change on every WebSocket frame.
const selectRestPatients = (state) => state.keyarData.keyarData;
const selectLivePatients = (state) => state.liveKeyarData.keyarData;

// Per-patientID cache so a merged patient keeps the SAME object reference
// across selector recomputes when neither their REST nor their live source
// actually changed. Without this, every WS frame from ANY patient forces a
// brand-new object for EVERY merged (REST+WS) patient — since Immer gives
// the whole livePatients array a new top-level reference on any one
// patient's mutation — which in turn defeats every reference-equality
// check downstream (React.memo comparators, the CentralGraphLayout
// patientMapRef diffing, etc.), causing all merged patients to be treated
// as "changed" on every single frame regardless of patient count.
const mergeCache = new Map();

export const selectMergedKeyarData = createSelector(
    [selectRestPatients, selectLivePatients],
    (restPatients, livePatients) => {
        const restIDs = new Set(restPatients.map((p) => p.patientID));
        const onlyLive = livePatients.filter((p) => !restIDs.has(p.patientID));

        const merged = restPatients.map((restPatient) => {
            const livePatient = livePatients.find((p) => p.patientID === restPatient.patientID);
            if (!livePatient) return restPatient;

            const cached = mergeCache.get(restPatient.patientID);
            if (cached && cached.rest === restPatient && cached.live === livePatient) {
                return cached.result;
            }

            const paramTypes = new Set([
                ...restPatient.keyarData.map((p) => p.param_type),
                ...livePatient.keyarData.map((p) => p.param_type),
            ]);
            const keyarData = Array.from(paramTypes).map((param_type) => {
                const restParam = restPatient.keyarData.find((p) => p.param_type === param_type);
                const liveParam = livePatient.keyarData.find((p) => p.param_type === param_type);
                if (!liveParam) return restParam;
                if (!restParam) return liveParam;
                return {
                    param_type,
                    latest_data: { ...restParam.latest_data, ...liveParam.latest_data },
                    meta_data: { ...restParam.meta_data, ...liveParam.meta_data },
                    serial_number: liveParam.serial_number || restParam.serial_number,
                };
            });

            // lastFrameAt comes from the live side only (REST patients never
            // set it) — carry it forward so consumers that key off it (e.g.
            // "has this patient's live data moved") see real updates.
            const result = { ...restPatient, keyarData, lastFrameAt: livePatient.lastFrameAt };
            mergeCache.set(restPatient.patientID, { rest: restPatient, live: livePatient, result });
            return result;
        });

        // Drop cache entries for patients no longer in the REST list, so a
        // long-running session doesn't accumulate stale entries forever.
        if (mergeCache.size > restIDs.size) {
            for (const key of mergeCache.keys()) {
                if (!restIDs.has(key)) mergeCache.delete(key);
            }
        }

        return [...merged, ...onlyLive];
    }
);
